import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import styled from '@mui/material/styles/styled';
import { useTranslation } from 'react-i18next';
import { DidimIcon, TimeIcon, CaloryIcon } from '../../../../assets/images/activityIcons';
import { DidimLogo } from '../../../../assets/images/didimLogo';
import ExerciseDataItem from '../../../../pages/reports/components/exerciseDataItem';
import RadarChart from '../../../../components/charts/radarChart';

// Styled components
const PaperCard = styled(Box)(({ theme, background }) => ({
    borderRadius: '20px',
    padding: '20px',
    boxShadow: theme.palette.shadows?.custom?.card || '0px 2px 8px rgba(0, 0, 0, 0.1)',
    border: 'none',
    position: 'relative',
    background: background || theme.palette.figma.block,
}));

const DidimActivityCard = ({
    organizationName = '',
    playLogsData = [],
    skill = {
        endurance: 0,
        strength: 0,
        balance: 0,
        flexibility: 0,
        reaction: 0,
        cooperation: 0,
    },
    sx = {},
    background = null,
    onClick = () => { }
}) => {
    const theme = useTheme();
    const { t } = useTranslation();

    return (
        <PaperCard sx={sx} background={background} onClick={onClick}>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2.5,
                }}
            >
                <DidimLogo
                    size={94}
                    color={theme.palette.figma.white[100]}
                />
                {organizationName && (
                    <Typography
                        sx={{
                            color: theme.palette.figma.white[80],
                            fontSize: '14px',
                            fontWeight: 500,
                            lineHeight: 'normal',
                            letterSpacing: '-0.42px',
                        }}
                    >
                        {organizationName}
                    </Typography>
                )}
            </Box>

            <Box sx={{ display: 'flex', gap: 2.5 }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        flex: '1 0 0',
                        alignSelf: 'stretch',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'flex-start',
                            gap: '10px',
                            flex: '1 0 0',
                            alignSelf: 'stretch',
                        }}
                    >
                        <ExerciseDataItem
                            icon={DidimIcon}
                            value={playLogsData.reduce((sum, log) => sum + (log.stepCount || 0), 0)}
                            unit={t('didim')}
                        />

                        {/* Time */}
                        <ExerciseDataItem
                            icon={TimeIcon}
                            value={Math.floor(playLogsData.reduce((sum, log) => {
                                if (log.endPlayTime && log.startPlayTime) {
                                    const diffSeconds = (new Date(log.endPlayTime) - new Date(log.startPlayTime)) / 1000;
                                    return sum + diffSeconds;
                                }
                                return sum;
                            }, 0) / 60)}
                            unit={t('min')}
                        />

                        {/* Calories */}
                        <ExerciseDataItem
                            icon={CaloryIcon}
                            value={playLogsData.reduce((sum, log) => sum + (log.endCalories - log.startCalories || 0), 0)}
                            unit="kcal"
                        />
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '5px',
                        flex: '1 0 0',
                    }}
                >
                    {/* Physical Fitness Chart */}
                    <RadarChart
                        data={{
                            endurance: skill.endurance, // 심폐지구력
                            strength: skill.strength, // 근력근지구력
                            balance: skill.balance, // 평형성
                            flexibility: skill.flexibility, // 유연성
                            reaction: skill.reaction, // 순발력
                            cooperation: skill.cooperation, // 협응력
                        }}
                        size={100}
                        showLabels={true}
                        showBackground={true}
                        sx={{ width: 140, height: 160 }}
                    />
                </Box>
            </Box>
        </PaperCard>
    );
};

export default DidimActivityCard;
